<template>
  <div
    class="gw-checklist-item generic-border generic-border--gray-border generic-border--clickable"
    :class="mainClasses"
    @click="$emit('click')"
  >
    <!-- Main row displayed for each item -->
    <div class="gw-checklist-item__main-content">
      <div class="gw-checklist-item__title-buttons">
        <div class="gw-checklist-item__title-buttons__title">
          <div class="gw-checklist-item__number legend">
            {{ `${index + 1}.` }}
          </div>
          <div class="gw-checklist-item__text">
            {{ decodeHTML(item.text) }}
          </div>
        </div>
        <div class="gw-checklist-item__buttons v-centerer">
          <RouterLink
            v-if="promptProblem"
            :to="{ path: getPbReportingLink(0), query: { pbReportingGwData: pbReportData } }"
            class="gw-checklist-item__buttons__pb generic-border generic-border--text-btn generic-border--gray-border"
          >
            <Icon>{{ icons.addExtraBold }}</Icon>
            <div>{{ $t('message.PROBLEM') }}</div>
          </RouterLink>
          <Badge :color="problemsBadgeColor" @click.stop.native="showProblems = !showProblems">
            <!-- eslint-disable-next-line vue/no-parsing-error -->
            <span slot="badge">{{ item.problems.length <= 9 ? item.problems.length : '9+' }}</span>
            <div
              class="
              gw-checklist-item__buttons__problems
              generic-border
              generic-border--large-icon-button
              generic-border--gray-border
            "
            >
              <Icon>{{ icons.problemsMedium }}</Icon>
            </div>
          </Badge>
          <Badge :color="commentsBadgeColor" @click.stop.native="showComments = !showComments">
            <!-- eslint-disable-next-line vue/no-parsing-error -->
            <span slot="badge">{{ item.comments.length <= 9 ? item.comments.length : '9+' }}</span>
            <div
              class="
              gw-checklist-item__buttons__comments
              generic-border
              generic-border--large-icon-button
              generic-border--gray-border
            "
            >
              <Icon>{{ icons.commentMedium }}</Icon>
            </div>
          </Badge>
          <Badge :color="filesBadgeColor" @click.stop.native="displayFiles">
            <span slot="badge">{{ item.files.length }}</span>
            <div
              class="
              gw-checklist-item__buttons__pictures
              generic-border
              generic-border--large-icon-button
              generic-border--gray-border
            "
            >
              <Icon>{{ icons.galleryMedium }}</Icon>
            </div>
          </Badge>
        </div>
      </div>
      <div class="gw-checklist-item__sep" />
      <div v-if="item.type == CL_ITEM_TYPE.OKNOK" class="gw-checklist-item__ok-nok">
        <div v-if="!disabled" class="v-centerer">
          <button
            class="gw-checklist-item__ok-nok__nok generic-border generic-border--text-btn"
            :class="{
              'generic-border--red-border': item.ok === 2,
              'gw-checklist-item__ok-nok__nok--selected generic-border--red': item.ok === 0,
              'gw-checklist-item__ok-nok__nok--unselected generic-border--gray-border': item.ok === 1,
            }"
            @click="tick(0)"
          >
            <Icon>{{ icons.close }}</Icon>
            <span>{{ $t('message.GW_NOK') }}</span>
          </button>
          <button
            class="gw-checklist-item__ok-nok__ok generic-border generic-border--text-btn"
            :class="{
              'generic-border--green-border': item.ok === 2,
              'gw-checklist-item__ok-nok__ok--selected generic-border--green': item.ok === 1,
              'gw-checklist-item__ok-nok__ok--unselected generic-border--gray-border': item.ok === 0,
            }"
            @click="tick(1)"
          >
            <Icon>{{ icons.tickExtraBold }}</Icon>
            <span>{{ $t('message.GW_OK') }}</span>
          </button>

        </div>
        <div v-else>
          <span v-if="item.ok == 2" class="f-w f-h">●</span>
          <Icon v-else-if="item.ok == 0" class="red">{{ icons.nokFill }}</Icon>
          <Icon v-else-if="item.ok == 1" class="green">{{ icons.okFill }}</Icon>
        </div>
      </div>
      <div v-else class="gw-checklist-item__ok-nok" :class="{ nok: isNOK }">
        <IndicatorInput
          v-if="indicator"
          :indicator="indicator"
          :focused-date="indicatorRange.end"
          nano
          shown
          @overrun="dmpIndicatorOverrun = $event"
          @clickPrevDate="indicPrevDate"
          @clickNextDate="indicNextDate"
        />
      </div>
    </div>
    <!-- END Main row displayed for each item -->

    <!-- Additional content, files, comments, problems -->
    <div class="gw-checklist-item__additional-content">
      <TransitionExpand>
        <div v-if="showFiles">
          <div class="gw-checklist-item__files">
            <div
              v-for="(file, fileIdx) in files"
              :key="fileIdx"
              class="gw-checklist-item__files__file"
              @click="$emit('showPictures', item, undefined, undefined, undefined, fileIdx)"
            >
              <FileThumbnail :file="file" />
            </div>
          </div>
        </div>
      </TransitionExpand>

      <TransitionExpand>
        <div v-if="showComments">
          <div class="gw-checklist-item__comments">

            <div v-if="showFiles" class="gw-checklist-item__comments__sep" />

            <div v-for="comment in item.comments" :key="comment.id" class="gw-checklist-item__comment v-centerer">
              <EditableLabel
                v-model="comment.text"
                :placeholder="$t('message.COMMENT_PLACEHOLDER')"
                :disabled="disabled"
                use-tiptap-editor
                display-tiptap-menu
                display-tiptap-tasks
                @input="editComment(comment)"
              />

              <Badge
                :color="comment.files && comment.files.length > 0 ? SColors.mainTextColor : SColors.mediumGray"
                @click.stop.native="$emit('showPictures', comment, 'gwClItemComments/', true, true)"
              >
                <span slot="badge">{{ comment.files.length }}</span>
                <!-- eslint-disable-next-line max-len -->
                <div class="gw-checklist-item__buttons__pictures generic-border generic-border--large-icon-button generic-border--gray-border">
                  <Icon>{{ icons.galleryMedium }}</Icon>
                </div>
              </Badge>

              <div
                class="generic-border generic-border--large-icon-button generic-border--gray-border"
                @click="deleteComment(comment)"
              >
                <Icon>{{ icons.deleteMedium }}</Icon>
              </div>
            </div>
            <button
              v-if="!disabled"
              class="generic-border generic-border--text-btn generic-border--gray-border f-w"
              @click="addComment"
            >
              <div class="generic-border__left">
                <Icon class="generic-border__left-blue-icon">{{ icons.addExtraBold }}</Icon>
                <span>{{ $t('message.GW_ADD_COMMENT') }}</span>
              </div>
              <Icon class="generic-border__right-arrow">{{ icons.arrowDownLine }}</Icon>
            </button>
          </div>
        </div>
      </TransitionExpand>

      <TransitionExpand>
        <div v-if="showProblems">
          <div class="gw-checklist-item__problems">

            <div v-if="showComments" class="gw-checklist-item__problems__sep" />

            <ProblemItem
              v-for="(pb, pbIndex) in item.problems"
              :key="pb.id"
              :problem="pb"
              class="gw-checklist-item__problems__problem generic-border--gray-border"
              @removed="problemDeleted(pbIndex)"
            />
            <RouterLink
              :to="{ path: getPbReportingLink(0), query: { pbReportingGwData: pbReportData } }"
              class="
                generic-border
                generic-border--text-btn
                generic-border--gray-border
                gw-checklist-item__problems__add
              "
            >
              <div class="generic-border__left">
                <Icon class="generic-border__left-blue-icon">{{ icons.addExtraBold }}</Icon>
                <span>{{ $t('message.PROBLEM') }}</span>
              </div>
              <Icon class="generic-border__right-arrow">{{ icons.arrowDownLine }}</Icon>
            </RouterLink>
          </div>

        </div>
      </TransitionExpand>
    </div>
  </div>
</template>

<script>
/**
 * Item of a checklist
 * According to item.type, this will display OK/NOK options (type 0),
 * a numerical value to enter (type 1), a DMP indicator to edit (type 2).
 * User may create problems directly from this component.
 *
 * Be aware that GW indicators and DMP indicators behaviours differ :
 * DMP indicators values depend on the date (one value per day) whereas the value
 * of a GW indicator is attached to the Checklist item.
 * For example: Two item in the same step should display the same value of a DMP indicator
 * (ie: one changes, the other should change too)
 * But two items won't display the same value for a GW indicator
 *
 * @vue-prop {Object} item - Item data as an object { id, text, pictures: [], type }
 * @vue-prop {Number} index - Index in the list of items
 * @vue-prop {Number} stepIndex - Index of the step that contains the item
 * @vue-prop {Object} gw - Main GW that contains the steps that contain this item
 * @vue-prop {String} indicatorInitialDate - Date of the GW. Defaults to gw.date
 * @vue-prop {Boolean} disabled - true if the current item isn't editable
 * @vue-prop {Boolean} current - current item is the selected item in the checklist
 * @vue-prop {Boolean} focusable - current item can be selected
 * @vue-prop {Boolean} passed - true if the currently focused item is after this.item
 *
 * @vue-event {} click - Propagate click event
 * @vue-event {} edit - A problem has been edited
 * @vue-event {} tick - For type = 0, item.ok has been edited
 * @vue-event {} showPictures - Ask parent for the pictures editor
 */

import { createNamespacedHelpers } from 'vuex';
import toastr from 'toastr';
import { isEqual } from 'lodash';
// Vuex utils
import { BUFFER_FETCH_INDICATORS } from '@/store/action-types';
import { GET_FILE_DETAILS, FETCH_FILE_DATA } from '@/store/modules/file/action-types';
import {
  SET_GW_CL_ITEM_COMMENT,
  DELETE_GW_CL_ITEM_COMMENT,
  FETCH_GW,
} from '@/store/modules/gemba-walks/action-types';
// Components
import Badge from '@/components/badge.vue';
import EditableLabel from '@/components/editable-label.vue';
import FileThumbnail from '@/components/file-system/file-thumbnail.vue';
import Icon from '@/components/icon.vue';
import IndicatorInput from '@/components/indicator-input.vue';
import ProblemItem from '@/components/problem-item.vue';
import TransitionExpand from '@/components/transition-expand.vue';
// Mixins
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Import assets & utils
import { calert, decodeHTML, parseWorkingDays } from '@/utils/main';
import { GW_CHECKLIST_ITEM_TYPE as CL_ITEM_TYPE } from '@/utils/gw-utils';
import SocketMessages from '@/utils/classes/SocketMessages';
import { SColors } from '@/utils/constants';
import { RE_IMAGE_TYPE } from '@/utils/file';
import * as icons from '@/assets/icons/';

const { mapActions: gwMapActions } = createNamespacedHelpers('gembaWalks');

export default {

  name: 'GwChecklistItem',

  components: {
    Badge,
    EditableLabel,
    FileThumbnail,
    Icon,
    IndicatorInput,
    ProblemItem,
    TransitionExpand,
  },

  mixins: [mixinSocketHandler],

  props: {
    item: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    stepIndex: {
      type: Number,
      required: true,
    },
    gw: {
      type: Object,
      required: true,
    },
    indicatorInitialDate: {
      type: String,
      default: null,
    },
    disabled: Boolean,
    current: Boolean,
    focusable: Boolean,
    passed: Boolean,
  },

  data() {
    return {
      CL_ITEM_TYPE,
      icons,
      SColors,
      promptProblem: false,
      unpromptable: false,
      indicatorRange: {
        start: this.$moment(this.indicatorInitialDate || this.gw.date),
        end: this.$moment(this.indicatorInitialDate || this.gw.date),
      },
      dmpIndicatorOverrun: false,
      tickTimeout: null,
      // Show additional content
      showFiles: false,
      showComments: false,
      showProblems: false,
    };
  },

  computed: {
    /** Boolean: Whether this item is OK/NOK based on an indicator value */
    isIndicator() {
      return [CL_ITEM_TYPE.DMP_INDICATOR, CL_ITEM_TYPE.GW_INDICATOR].includes(this.item.type);
    },
    /** Object: Classes of this.$el */
    mainClasses() {
      return {
        disabled: this.disabled,
        'gw-checklist-item--focused': this.current,
        'generic-border--focused': this.current && !this.disabled,
        'generic-border--passed': this.passed,
        'ok-nok': this.item.type === CL_ITEM_TYPE.OKNOK,
        numerical: this.isIndicator,
      };
    },
    /** Object: indicator data as set in the state */
    indicator() {
      if (this.isIndicator) {
        const indic = this.$store.state.indicators[`${this.item.indicator}`];
        if (indic) {
          return indic;
        }
      }
      return null;
    },
    problemsBadgeColor() {
      return this.item.problems.length === 0 ? '#999999' : SColors.mainTextColor;
    },
    filesBadgeColor() {
      return this.item.files.length === 0 ? '#999999' : SColors.mainTextColor;
    },
    commentsBadgeColor() {
      return this.item.comments.length === 0 ? '#999999' : SColors.mainTextColor;
    },
    itemText() {
      return `${this.index + 1}. ${this.item.text}`;
    },
    /** String: JSON encoded data for problem reporting */
    pbReportData() {
      const gwStep = this.gw.steps[this.stepIndex];
      return JSON.stringify({
        gwId: this.gw.id,
        gwName: this.gw.name,
        stepId: gwStep.id,
        stepName: gwStep.name,
        stepTeam: gwStep.auditedTeam,
        stepZone: gwStep.zone,
        clItemId: this.item.id,
        clItemName: this.item.text,
      });
    },
    /** Does the indicator overruns its objective */
    indicatorOverrun() {
      if (!this.indicator) return false;
      return this.dmpIndicatorOverrun;
    },
    /** Should the current state of the item be highlighted as not OK */
    isNOK() {
      if (this.item.type === CL_ITEM_TYPE.OKNOK) {
        // NOK: 0, OK: 1, N/A: 2
        return this.item.ok === 0;
      }
      // NOK: indicator overruns objective
      return this.indicatorOverrun;
    },
    /** Array: File objects of this item */
    files() {
      return this.item.files.map(id => this.$store.state.file.files[id] || { id });
    },
  },

  watch: {
    /** Indicator object was updated => update the ok value */
    indicator(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.$nextTick(() => {
          this.delayTick(this.indicatorOverrun ? 0 : 1, false);
        });
      }
    },
    /** Item is considered Not OK when overrunning the objective */
    indicatorOverrun(doesOverrun) {
      if (this.item.type !== CL_ITEM_TYPE.OKNOK) {
        const okValue = doesOverrun ? 0 : 1;
        if (this.item.ok !== okValue) {
          this.delayTick(okValue, false);
        }
      }
    },
    isPbReportingDisplayed() {
      // A problem, most likely related to this GW, was updated
      if (!this.isPbReportingDisplayed) {
        this.$store.dispatch(`gembaWalks/${FETCH_GW}`, { id: this.gw.id });
      }
    },
    isPbSheetDisplayed() {
      // A problem, most likely related to this GW, was updated
      if (!this.isPbSheetDisplayed) {
        this.$store.dispatch(`gembaWalks/${FETCH_GW}`, { id: this.gw.id });
      }
    },
  },

  created() {
    this.refreshIndicator();

    this.handleSocketMessage(SocketMessages.CC_MSGS.INDICATORS, (msg) => {
      if (Number(this.item.indicator) === Number(msg.id)) {
        this.refreshIndicator();
      }
    });
  },

  beforeDestroy() {
    clearTimeout(this.tickTimeout);
  },

  methods: {
    decodeHTML,
    /** Prevents sending multiple unnecessary XHR */
    delayTick(valueTicked, focusNext = true) {
      clearTimeout(this.tickTimeout);
      this.tickTimeout = setTimeout(() => {
        this.tick(valueTicked, focusNext);
      }, 150);
    },
    /**
     * Change OK/NOK state
     * @param  {Number}  valueTicked - 0: NOK, 1: OK, 2: Not applicable
     * @param  {Boolean} [focusNext=true] - By default, setting 0 or 1 to an item
     *                                      will move the focus to the next item
     */
    tick(valueTicked, focusNext = true) {
      if (!this.disabled) {
        valueTicked = Number(valueTicked);
        if (this.item.type === CL_ITEM_TYPE.OKNOK) {
          this.item.ok = this.item.ok === valueTicked ? 2 : valueTicked;
        } else {
          this.item.ok = valueTicked;
        }
        if (this.item.ok === 0 && this.focusable && focusNext && !this.$store.state.device.isSmartphonePortrait) {
          this.unpromptable = false;
          this.promptProblem = true;
          setTimeout(() => {
            this.unpromptable = true;
          }, 1);
        } else {
          this.promptProblem = false;
        }
        this.$emit('tick', this.item, focusNext);
      }
    },
    /** The problem has been succesfully deleted */
    problemDeleted(pbIndex) {
      this.item.problems.splice(pbIndex, 1);
    },
    /** Fetch images data */
    displayFiles() {
      if (this.showFiles || !this.item.files.length) {
        this.showFiles = false;
        return;
      }
      this.showFiles = true;
      this.$store.dispatch(`file/${GET_FILE_DETAILS}`, { ids: this.item.files })
        .then(() => {
          const promises = [];
          this.files.forEach((file) => {
            if (!RE_IMAGE_TYPE.test(file.type)) return;
            promises.push(this.$store.dispatch(`file/${FETCH_FILE_DATA}`, { fileId: file.id }));
          });
          Promise.all(promises);
        })
        .catch((err) => {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
          throw err;
        });
    },
    // Indicator-related methods
    /** Refresh indicator data & values */
    refreshIndicator() {
      if (this.isIndicator && this.item.indicator) {
        this.indicatorRange.start.set({ h: 0, m: 0, s: 0 });
        this.indicatorRange.end.set({ h: 23, m: 59, s: 59 });
        this.$store.dispatch(BUFFER_FETCH_INDICATORS, {
          indicatorId: [this.item.indicator],
          range: this.indicatorRange,
          values: true,
        });
      }
    },
    /** Handle changing the date of the indicator-input component */
    indicPrevDate() {
      this.indicatorRange.end = this.indicatorRange.end.clone().subtract(1, 'd');
      this.indicatorRange.start.subtract(1, 'd');
      this.indicDateChanged(this.indicPrevDate);
    },
    indicNextDate() {
      this.indicatorRange.end = this.indicatorRange.end.clone().add(1, 'd');
      this.indicatorRange.start.add(1, 'd');
      this.indicDateChanged(this.indicNextDate);
    },
    indicDateChanged(callback) {
      const wd = this.indicator.workingDays
        ? parseWorkingDays(this.indicator.workingDays)
        : this.App.user.workingDays || [true, true, true, true, true, true, true];
      if (!wd[this.indicatorRange.end.isoWeekday() - 1]) {
        callback();
        return;
      }
      this.refreshIndicator();
    },
    // Comments-related methods
    /** Create a new comment */
    addComment() {
      this.SET_GW_CL_ITEM_COMMENT({ id: 0, gwClItemId: this.item.id, text: '' });
    },
    /** Edit a comment */
    editComment(comment) {
      this.SET_GW_CL_ITEM_COMMENT({ id: comment.id, gwClItemId: comment.gwClItem, text: comment.text });
    },
    /** Delete a comment */
    deleteComment(comment) {
      calert(this.$t('message.CONFIRM_DELETE_COMMENT'), [
        {
          title: this.$t('message.DELETE'),
          sClass: 'red',
          action: () => {
            this.DELETE_GW_CL_ITEM_COMMENT({ id: comment.id, gwClItemId: comment.gwClItem });
          },
        },
        {
          title: this.$t('message.NO'),
        },
      ]);
    },
    ...gwMapActions({
      SET_GW_CL_ITEM_COMMENT,
      DELETE_GW_CL_ITEM_COMMENT,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.gw-checklist-item {
  cursor: pointer;
  padding: .85rem;
  position: relative;
  border: 1px solid rgb(230, 225, 224);

  &__main-content {
    display: flex;
    align-items: center;
  }

  &__title-buttons {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0px;

    &__title {
      display: flex;
      align-items: center;
      flex: 1;
      min-width: 0px;
    }
  }

  &__number, &__buttons, &__sep, &__ok-nok {
    flex: 0 0 auto;
  }

  &__text {
    flex: 1;
    margin-left: .3rem;
    min-width: 0px;
    overflow: hidden;
  }

  &__buttons {
    &__pb, &__comments, &__pictures, &__problems {
      margin-left: .7rem;
    }
  }

  &__sep {
    margin: 0 1.2rem;
    width: 1px;
    height: 2.4rem;
    background: $color-gray-background-hover;
  }

  &__ok-nok {
    width: 12rem;

    &__ok {
      margin-left: 0.8rem;
      color: $color-shizen-green;
    }

    &__nok {
      color: $color-shizen-red;
    }

    &__ok--selected, &__ok--unselected, &__nok--selected, &__nok--unselected {
      color: inherit;
    }

    &__ok--unselected, &__nok--unselected {
      opacity: 0.6;
    }

    .indicator-input {
      position: static;

      &.nano {
        input.indicator-input__nano__input {
          width: 100%;
          margin: 0 .5rem;
          padding: 0.6rem;
          text-align: center;
        }

        .indicator-input__values__current {
          width: 100%;

          .indicator-input__values__current__single.v-centerer {
            width: 80%;
          }
        }
      }
    }
  }

  &__files , &__comments, &__problems {
    padding: 0.7rem;
  }

  &__comments .gw-checklist-item__comments__sep, &__problems .gw-checklist-item__problems__sep {
    width: 100%;
    height: 1px;
    background: $color-gray-background-hover;
    margin-bottom: 1rem;
  }

  &__files {
    box-sizing: border-box;
    display: flex;

    &__file {
      margin-right: 1rem;
      height: 100%;

      .file-thumbnail {
        &__preview {
          &__img-elmt {
            height: 150px;
            display: block;
          }
        }
      }
    }
  }

  &__comment {
    margin-bottom: 1em;

    > * {
      flex: 0 0 auto;

      &.editable-label {
        flex: 1;
        min-width: 0px;
      }

      &.picture-carousel {
        flex: 0 0 20%;
        height: 4em;
        padding: 0px 4%;
      }

      &.generic-border--large-icon-button:last-child {
        margin-left: .7rem;
      }
    }
  }

  &__problems {
    &__problem.problem-item {
      margin: 0 0 .7rem 0;
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .gw-checklist-item {

    &__title-buttons {
      flex-flow: column;
      align-items: unset;
    }

    &__buttons {
      &.v-centerer {
        display: none;
      }
    }

    &--focused {
      .gw-checklist-item__title-buttons {
        &__title {
          margin-bottom: 1.5rem;
        }
      }

      .gw-checklist-item__buttons {
        &.v-centerer {
          display: flex;
        }
      }
    }

    &__sep {
      margin: 0 0.7rem 0 1.2rem;
    }

    &__ok-nok {
      width: 7rem;

      &__ok, &__nok {
        span {
          display: none;
        }

        &.generic-border--text-btn .icon {
          margin-right: 0px;
        }
      }

      .indicator-input.nano {
        padding: 0px;

        input.indicator-input__nano__input {
          margin: 0;
          padding: 0.7rem;
        }

        .indicator-input__nano__clickoverlay {
          margin: 0;
        }
      }
    }
  }
}
</style>
